<template>
  <div class="point_list">
    <div class="deco">
      <h2 class="access_hidden">포인트 전환 상세 내역</h2>
      <period-setting :cateArr="cateArr"></period-setting>
      <div v-if="pointList.list != null" class="list">
        <table>
          <col class="statue" />
          <col class="contents" />
          <thead>
            <tr class="access_hidden">
              <th class="statue">종류</th>
              <th class="contents">처리 일시, 내용, 금액</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(el, idx) in pointList.list" :key="idx">
              <td class="status" :class="pointStatusClassName(el).default">
                <v-icon :clsName="pointStatusClassName(el).icon" />
                {{ getPointStatusText(el.pointStatus) }}
              </td>
              <td class="contents">
                <div class="contents_header">
                  <span v-if="el.regDate" class="date">{{ el.regDate }}</span>
                  <span v-if="el.expiryDate" class="date"
                    >{{ $t('pointHistory.expiryDate') }}: {{ el.expiryDate }}</span
                  >
                </div>
                <div>
                  <span class="content">{{ getContent(el.content) }}</span>
                  <span v-if="el.content.extraInfo" class="sub_content">{{ getSubContent(el.content) }}</span>
                </div>
                <span class="point" :class="pointStatusClassName(el).default">
                  {{ el.pointStatus === 1 ? '+' : '-' }} {{ addComma(el.point) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <pagination
          @custom-change="changeOffset"
          :offset="pointList.pageNo"
          :total-item-cnt="pointList.totalCount"
          :list-item-size="10"
          :page-size="pointList.pageSize"
        >
        </pagination>
      </div>
      <p v-else class="none"><v-icon clsName="icon_none" /><span v-html="errorMessage"></span></p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PeriodSetting from '@/components/modules/pointHistory/PeriodSetting';
import Pagination from '@/components/modules/common/Pagination';
import VIcon from '@/components/atoms/VIcon';
import { pointHistoryErrorMsg } from '@/utils/errorMsg';
import mixin from '@/utils/mixin';
import { dateToYYYYMMDD } from '@/utils';
import { POINT_STATUS_CLASS } from '@/consts/consts';

export default {
  mixins: [mixin],
  components: {
    PeriodSetting,
    Pagination,
    VIcon,
  },
  data() {
    return {
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('point', [
      'pointList',
      'pointListPageSize',
      'pointListPageNo',
      'pointListStartDate',
      'pointListEndDate',
      'pointListStatus',
    ]),
    cateArr() {
      return this.$i18n.t('pointHistory.statusList');
    },
    pointStatusClassName() {
      return (item) => {
        const pointStatusName = POINT_STATUS_CLASS[item.pointStatus];
        return { default: pointStatusName, icon: `icon_${pointStatusName}` };
      };
    },
    toYYYYMMDD() {
      return (dateString) => {
        return dateToYYYYMMDD(new Date(dateString.replace(/\./g, '/')), '.');
      };
    },
    getContent() {
      return ({ chargeType, chargeTypeName: contentType, subChargeTypeName = '', regDate: date = '', extraInfo }) => {
        // regDate = format 2023.04.12
        let year = '';
        let month = '';
        let day;

        if (date) {
          const dataObj = new Date(date.replace(/\./g, '/'));
          year = dataObj.getFullYear();
          month = dataObj.getMonth() + 1;
          day = dataObj.getDate();

          if (month < 10) {
            month = `0${month}`;
          }

          if (day < 10) {
            day = `0${day}`;
          }

          if (this.$i18n.locale !== 'ko' && this.$i18n.locale !== 'ko-KR') {
            // 2023.01.04 -> MM/DD/YYYY
            date = `${month}/${day}/${year}`;
          }
        }

        // creatorSanction: '크리에이터 제재', policyViolation: '운영정책 위반'
        if (contentType === 'cancelEarn' && ['creatorSanction', 'policyViolation'].includes(extraInfo)) {
          return this.$i18n.t(`pointHistory.pointsReclaim`, { date, subChargeTypeName, year, month });
        } else {
          return this.$i18n.t(`pointHistory.${contentType}`, { date, subChargeTypeName, year, month });
        }
      };
    },
    getSubContent() {
      return ({ chargeTypeName, extraInfo }) => {
        if (!extraInfo) {
          return '';
        }
        // pointTransferOperations (운영으로 인한 이관 적립), pointTransferAccountHolderChange (명의변경)
        let isUseNicknameType = [
          'earn',
          'chargeCampaignPoint',
          'chargeLiveDonation',
          'lostPoint',
          'chargeNConnectReward',
        ].includes(chargeTypeName);
        if (
          ['earn', 'chargeCampaignPoint'].includes(chargeTypeName) &&
          ['pointTransferOperations', 'pointTransferAccountHolderChange'].includes(extraInfo)
        ) {
          isUseNicknameType = false;
        } else if (
          ['chargeLiveDonation'].includes(chargeTypeName) &&
          ['pointTransferOperations', 'pointTransferAccountHolderChange'].includes(extraInfo)
        ) {
          isUseNicknameType = false;
        } else if (
          ['lostPoint'].includes(chargeTypeName) &&
          ['creatorSanction', 'policyViolation'].includes(extraInfo)
        ) {
          isUseNicknameType = false;
        }
        return isUseNicknameType ? extraInfo : this.$i18n.t(`pointHistory.subContentExtra.${extraInfo}`);
      };
    },
    getPointStatusText() {
      return (pointStatus) => {
        const pointStatusName = POINT_STATUS_CLASS[pointStatus];
        return this.$i18n.t(`pointHistory.${pointStatusName}`);
      };
    },
  },
  watch: {
    $route(to, from) {
      if (to.path !== from.path) {
        this.init();
      }
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapActions('point', ['getPointHistory', 'setPointListPageNo', 'setPointListStartDate', 'setPointListEndDate']),
    historyParameter() {
      return {
        startDate: this.pointListStartDate,
        endDate: this.pointListEndDate,
        pointStatus: this.pointListStatus,
        pageNo: this.pointListPageNo,
        pageSize: this.pointListPageSize,
      };
    },
    async init() {
      this.setPointListEndDate(this.$moment().format('YYYY-MM-DD'));
      this.setPointListStartDate(this.$moment().add(-1, 'months').add(1, 'days').format('YYYY-MM-DD'));
      const apiReturn = await this.getPointHistory(this.historyParameter());
      this.errorMessage = pointHistoryErrorMsg(apiReturn);
    },
    async changeOffset(offset) {
      this.setPointListPageNo(offset);
      const apiReturn = await this.getPointHistory(this.historyParameter());
      this.errorMessage = pointHistoryErrorMsg(apiReturn);
    },
  },
};
</script>

<style lang="scss" scoped>
.contents_header {
  display: flex;
  .date {
    &:nth-child(2) {
      &::before {
        content: '|';
        padding: 0 10px;
        width: 1px;
        height: 10px;
        color: #ddd;
        font-size: 12px;
      }
    }
  }
}

@media all and (max-width: 767px) {
  .contents_header {
    flex-direction: column;
    margin-bottom: 13px;
    .date {
      padding: 0;
      &:nth-child(2) {
        &::before {
          content: '';
          padding: 0;
        }
      }
    }
  }
}
</style>
